import { useEffect, useState } from "react";

interface TrafficStats {
  configured: true;
  provider: "DataFast";
  activeVisitors: number;
  totalVisitors: number;
  preDataFastHttpResponses?: number;
  publicStatsUrl?: string;
  stale: boolean;
  updatedAt: string;
}

const STALE_ACTIVE_VISITOR_MAX_AGE_MS = 15 * 60_000;

function isTrafficStats(value: unknown): value is TrafficStats {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<TrafficStats>;
  return candidate.configured === true
    && candidate.provider === "DataFast"
    && Number.isSafeInteger(candidate.activeVisitors)
    && (candidate.activeVisitors ?? -1) >= 0
    && Number.isSafeInteger(candidate.totalVisitors)
    && (candidate.totalVisitors ?? -1) >= 0
    && typeof candidate.stale === "boolean"
    && typeof candidate.updatedAt === "string";
}

export default function TrafficProof() {
  const [stats, setStats] = useState<TrafficStats | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const load = async () => {
      try {
        const response = await fetch("/api/traffic", { signal: controller.signal });
        if (!response.ok) {
          if (active) setStats(null);
          return;
        }
        const body: unknown = await response.json();
        if (active) setStats(isTrafficStats(body) ? body : null);
      } catch (error) {
        if (active && !(error instanceof DOMException && error.name === "AbortError")) setStats(null);
      }
    };

    void load();
    const interval = window.setInterval(() => void load(), 60_000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
    };
  }, []);

  if (!stats) return null;

  const updatedAtMs = Date.parse(stats.updatedAt);
  const staleAgeMs = Date.now() - updatedAtMs;
  const showActiveVisitors = !stats.stale || (
    Number.isFinite(updatedAtMs)
    && staleAgeMs >= 0
    && staleAgeMs <= STALE_ACTIVE_VISITOR_MAX_AGE_MS
  );
  const onlineWording = stats.stale ? "recently online" : "online";
  const historicalContext = stats.preDataFastHttpResponses === undefined
    ? ""
    : ` ${stats.preDataFastHttpResponses.toLocaleString("en-US")} earlier HTTP responses were recorded before DataFast tracking; these include requests rather than unique visitors.`;
  const activeVisitorLabel = showActiveVisitors
    ? `${stats.activeVisitors.toLocaleString("en-US")} ${onlineWording}; `
    : "";
  const accessibleLabel = `${activeVisitorLabel}${stats.totalVisitors.toLocaleString("en-US")} visitors tracked by DataFast.${historicalContext}`;
  const title = showActiveVisitors
    ? `${stats.stale ? "Recently verified" : "Live"} DataFast audience statistics.${historicalContext}`
    : `DataFast visitor total. The active visitor count is hidden because its last update is older than 15 minutes.${historicalContext}`;
  const contents = (
    <>
      {showActiveVisitors ? (
        <>
          <span className={`earth-traffic-live${stats.stale ? " is-stale" : ""}`}>
            <i aria-hidden="true" />
            <b>{stats.activeVisitors.toLocaleString("en-US")}</b> {onlineWording}
          </span>
          <span className="earth-traffic-separator" aria-hidden="true">·</span>
        </>
      ) : null}
      <span><b>{stats.totalVisitors.toLocaleString("en-US")}</b> visitors</span>
      <span className="earth-traffic-source">DataFast{stats.publicStatsUrl ? " ↗" : ""}</span>
    </>
  );

  return stats.publicStatsUrl ? (
    <a
      className="earth-traffic-proof"
      href={stats.publicStatsUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${accessibleLabel} Open public DataFast statistics.`}
      title={title}
    >
      {contents}
    </a>
  ) : (
    <span className="earth-traffic-proof" role="status" aria-live="polite" aria-label={accessibleLabel} title={title}>
      {contents}
    </span>
  );
}
